import React, { useState, useContext, useMemo, useEffect, useRef } from "react";
import type { DatePickerProps } from 'antd';
import { DatePicker, Space, ConfigProvider, Button, Select, Modal, Upload, Checkbox } from 'antd';
import { saveProjectMilestonesApi, getMilestoneCircleNodeApi, getMilestoneInvoiceTimeLimitApi, uploadFileApi } from '@/services'
import { useRequest } from 'ahooks'
import dayjs from 'dayjs';
import { message } from 'antd';
import { ProjectDetailContext } from "../constant"
import { k_permission } from "../project"
import { ThEmpty } from '@/components';
import { parseTime } from "@/utils"
import HistoryModal from "./history"
import {
    HistoryOutlined,
    UploadOutlined,
    DeleteOutlined
} from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { getMinDateForMilestone, getPrevNonSpecialNodeIndex, isLastGroupNodeCompleted } from '../project'
import type { UploadRequestOption } from "rc-upload/lib/interface";


interface IProps{
    values?: API.PROJECT.Milestone[]
    projectID?: string | number,
    plotID?: number,
    isFirstPlot?: boolean,
    isBatch?: boolean, // 主创批量编辑
    isPlanList?: boolean, // 计划列表
    onUpdate: () => void
    onCancel: () => void
}
type Pickchange = DatePickerProps['onChange']
interface MilestoneEditItem extends API.PROJECT.Milestone{
    isEdit: boolean,
    isWarn?: boolean,
    originalStatus?: number,
}

export const k_milestoneStatus = {
    unstart: 0,
    finished: 1,
    overdue: 2
}
export const getMilestoneStatusLabel = (status?: number) => {
    if (status === k_milestoneStatus.overdue) return <span className="text-red-500">已延期</span>
    if (status === k_milestoneStatus.finished) return <span className="text-green-500">已完成</span>
    return <span className="text-gray-800">未完成</span>
}
export const getMilestoneStatusTextColor = (status?: number) => {
    if (status === k_milestoneStatus.overdue) return "text-red-500"
    if (status === k_milestoneStatus.finished) return "text-green-500"
    return "text-gray-800"
}
export const getMilestoneStatusBgColor = (status: number) => {
    if (status === k_milestoneStatus.overdue) return "bg-red-500"
    if (status === k_milestoneStatus.finished) return "bg-green-500"
    return "bg-gray-800"
}
export const statusOptions = [{
    value: k_milestoneStatus.unstart,
    label: <span className="text-gray-800">未完成</span>
}, {
    value: k_milestoneStatus.finished,
    label: <span className="text-green-500">已完成</span>
    }]

const getStatusText = (status: number) => {
    if (status === k_milestoneStatus.finished) return "已完成"
    if (status === k_milestoneStatus.unstart) return "未完成"
    return ""
}
const k_circleNodeParentCode = "69"
const k_circleNodeCode = "circle-node-69"
const Index: React.FC<IProps> = (props) => {
    const [searchParams] = useSearchParams();
    const invoiceToken = searchParams.get('token') || '';
    const currentDate = useRef(new Date())
    const [messageApi, contextHolder] = message.useMessage();
    const [modalApi, modalContextHolder] = Modal.useModal();
    const [list, setList] = useState<MilestoneEditItem[]>([])
    const [circleNodeData, setCircleNodeData] = useState<API.PROJECT.CircleMilestone>({
        ID: 0,
        milestoneCode: k_circleNodeCode,
        standardDate: "",
        milestoneName: "外协交圈时间",
        status: 0,
        actualDate: "",
    })

    const { runAsync: runSaveMilestones, loading } = useRequest(saveProjectMilestonesApi, {
        manual: true,
        onError: (e) => {
            if(typeof e === 'string'){
                messageApi.error(e)
                return
            }
            messageApi.error("保存失败")
        }
    })
    const { runAsync: runAsyncCircleNode, loading: loadingCircleNode } = useRequest(getMilestoneCircleNodeApi, {
        defaultParams: [
            props.projectID || '',
            props.plotID
        ],
        onSuccess: (res) => {
            if (!res || !res.ID || res.ID === 0) return
            setCircleNodeData(res)
        }
    })
    const { data: invoiceTimeLimitMap } = useRequest(getMilestoneInvoiceTimeLimitApi, {
        defaultParams: [
            props.projectID || '',
        ],
        ready: !!props.isPlanList && !!props.projectID,
        refreshDeps: [props.isPlanList, props.projectID]
    })

    useEffect(() => {
        if (!props.values) return
        let newList = props.values.map(v => {
            if (v.milestoneCode === "start_time") return {
                ...v,
                originalStatus: v.status,
                isEdit: false,
                actualDate: v.actualDate || v.milestoneDate || ""
            }
            return { ...v, isEdit: false, originalStatus: v.status }
        })
        setList(newList)
    }, [props.values])
    
    const { permissions } = useContext(ProjectDetailContext)

    // 对同组节点根据时间进行排序， 如果完成时间有值， 则根据完成时间排序， 否则根据计划时间排序
    const sortSpecialNodes = (nodeList: MilestoneEditItem[], currentIndex: number) => {
        const currentItem = nodeList[currentIndex];
        if (!currentItem || !currentItem.groupCode) return nodeList;
        
        // 找到相同groupCode且相邻的连续元素组
        const targetGroupCode = currentItem.groupCode;
        let startIndex = currentIndex;
        let endIndex = currentIndex;
        
        // 向左找连续的同组元素
        while (startIndex > 0 && nodeList[startIndex - 1].groupCode === targetGroupCode) {
            startIndex--;
        }
        
        // 向右找连续的同组元素
        while (endIndex < nodeList.length - 1 && nodeList[endIndex + 1].groupCode === targetGroupCode) {
            endIndex++;
        }
        
        // 如果只有一个元素，不需要排序
        if (startIndex === endIndex) return nodeList;
        
        // 提取同组元素并根据时间排序
        const groupNodes = nodeList.slice(startIndex, endIndex + 1);
        groupNodes.sort((a, b) => {
            // 获取a的排序日期：优先actualDate，否则milestoneDate
            const dateA = a.actualDate ?
                new Date(a.actualDate).getTime() :
                (a.milestoneDate ? new Date(a.milestoneDate).getTime() : 0);
            // 获取b的排序日期：优先actualDate，否则milestoneDate
            const dateB = b.actualDate ?
                new Date(b.actualDate).getTime() :
                (b.milestoneDate ? new Date(b.milestoneDate).getTime() : 0);
            return dateA - dateB;
        });
        
        // 替换原列表中的同组元素
        const result = [...nodeList];
        for (let i = startIndex; i <= endIndex; i++) {
            result[i] = groupNodes[i - startIndex];
        }
        
        return result;
    };

    const onDateChange = (item: API.PROJECT.Milestone, date: any , dateStr: string | string[], index: number) => {
        const milestoneDateStr = parseTime(dateStr as string, "{y}-{m}-{d}")
        const newlist = list.map(v => {
            if (v.milestoneCode !== item.milestoneCode) return v
            return {
                ...v,
                isEdit: true,
                milestoneDate: milestoneDateStr,
                actualDate: v.milestoneCode === "start_time" ? milestoneDateStr : v.actualDate,
            }
        })
        
        // 对连续的特殊节点进行排序
        if ((newlist[index].groupCode)) {
            const sortedList = sortSpecialNodes(newlist, index);
            setList(sortedList);
            return;
        }
        
        setList(newlist)
    }
    const onActualDateChange = (item: API.PROJECT.Milestone, date: any, dateStr: string | string[], index: number) => {
        const completedDateStr = parseTime(dateStr as string, "{y}-{m}-{d}")

        const newlist = list.map(v => {
            if (v.milestoneCode !== item.milestoneCode) return v
            return {
                ...v,
                isEdit: true,
                actualDate: completedDateStr
            }
        })
        
        // 对连续的特殊节点进行排序
        if ((newlist[index].groupCode)) {
            const sortedList = sortSpecialNodes(newlist, index);
            setList(sortedList);
            return;
        }

        setList(newlist)
    }

    // 甲方确认单上传处理
    const addConfirmationAttach = (milestoneCode: string, attach: API.PROJECT.MilestoneAttachment) => {
        setList(prev => prev.map(v => {
            if (v.milestoneCode !== milestoneCode) return v
            const existing = v.confirmationAttachs || []
            return { ...v, confirmationAttachs: [...existing, attach], isEdit: true }
        }))
    }

    const removeConfirmationAttach = (milestoneCode: string, index: number) => {
        setList(prev => prev.map(v => {
            if (v.milestoneCode !== milestoneCode) return v
            const existing = [...(v.confirmationAttachs || [])]
            existing.splice(index, 1)
            return { ...v, confirmationAttachs: existing, isEdit: true }
        }))
    }

    const handleUploadFile = async (options: UploadRequestOption, milestoneID = 0, milestoneCode:string) => {
        const { file, onSuccess, onError } = options
        try {
            const result = await uploadFileApi(file as File)
            const uploadedFile = result.file
            const newAttach: API.PROJECT.MilestoneAttachment = {
                standardMilestoneDateID: milestoneID || 0,
                milestoneCode: milestoneCode,
                fileUploadAndDownloadID: uploadedFile.ID,
                plotID: props.plotID || 0,
                fileName: uploadedFile.name,
            }
            addConfirmationAttach(milestoneCode, newAttach)
            onSuccess?.(result)
            messageApi.success('上传成功')
        } catch (error) {
            onError?.(error as any)
            messageApi.error('上传失败')
        }
    }

    const onStatueChange = (val: number, item: API.PROJECT.Milestone, index: number) => {
        let dateStr = ""
        if (val === k_milestoneStatus.finished) {
            const mindate = getMinDateForMilestone(list, index, true)
            const defaultdate = getDefaultDate(item)
            if (index === 1) {
                dateStr = defaultdate
            } else if (mindate && dayjs(defaultdate).isAfter(mindate)) {
                dateStr = defaultdate
            }
            // let showLastWarn = true
            // list.forEach(v => {
            //     if(v.status !== k_milestoneStatus.finished && v.milestoneCode !== item.milestoneCode) {
            //         showLastWarn = false
            //     }
            // })
            // setShowLastMilestoneWarn(showLastWarn)

            if (item.milestoneCode === k_circleNodeParentCode && circleNodeData.status === k_milestoneStatus.unstart) {
                messageApi.error('请先设置外协交圈时间~');
                return
            }

            // 甲方确认单必传校验
            // if (item.isRequiredOrg && (!item.confirmationAttachs || item.confirmationAttachs.length === 0)) {
            //     messageApi.error('请先上传甲方确认单~');
            //     return
            // }
        }
        const completedDateStr = parseTime(dateStr as string, "{y}-{m}-{d}")

        setList((vals) => {
            return vals.map(v => {
                if (v.milestoneCode !== item.milestoneCode) return v
                return {
                    ...v,
                    status: val,
                    isEdit: true,
                    actualDate: completedDateStr
                }
            })
        })
    }

    const onAdvanceInvoiceChange = (checked: boolean, item: API.PROJECT.Milestone) => {
        setList((vals) => {
            return vals.map(v => {
                if (v.milestoneCode !== item.milestoneCode) return v
                return {
                    ...v,
                    isEdit: true,
                    isAdvanceInvoice: checked
                }
            })
        })
    }

    const onSave = () => {
        if (loading || (!hasEditPermission && !isOpadmin)) return
        const nullItem = list.find(v => !v.milestoneDate)
        if (nullItem) {
            messageApi.error('计划时间不可为空~');
            return
        }
        // 所有计划时间必须在开始时间之后
        let validateFail = false, isLessStartTime = false, isBeforeLastMilestone = false, isCircleUnstart = false
        const startItem = list.find((v) => v.milestoneCode === "start_time")
        
        
        const circleCompletionItem = circleNodeData
        const milestoneList = list.filter((v) => v.milestoneCode !== k_circleNodeCode)


        const circleNodeParentItem = list.find((v) => v.milestoneCode === k_circleNodeParentCode)
        if (circleNodeParentItem && !circleNodeData.standardDate) {
            messageApi.error('请先设置外协交圈时间~');
            return
        }

        
        
        const params = milestoneList.map((v, index) => {
            if (v.status === k_milestoneStatus.finished && v.milestoneCode !== "start_time" && !v.actualDate) {
                validateFail = true
            }
            let milestoneDateStr = v.milestoneDate
            if (milestoneDateStr) {
                milestoneDateStr = parseTime(milestoneDateStr, "{y}-{m}-{d}")
            }
            let actualDateStr = v.actualDate
            if (actualDateStr) {
                actualDateStr = parseTime(actualDateStr, "{y}-{m}-{d}")
                if (!isBeforeLastMilestone) {
                    isBeforeLastMilestone = checkIsBeforeLastMilestone(index, true)
                }
            }
            if (!isBeforeLastMilestone) {
                isBeforeLastMilestone = checkIsBeforeLastMilestone(index)
            }

            if (startItem && v.milestoneDate && new Date(startItem.milestoneDate).getTime() > new Date(v.milestoneDate).getTime()) {
                isLessStartTime = true
            }

            if(v.milestoneCode === k_circleNodeParentCode && v.status === k_milestoneStatus.finished && circleNodeData.status === k_milestoneStatus.unstart) {
                isCircleUnstart = true
            }

            return {
                milestoneCode: v.milestoneCode,
                standardDate: milestoneDateStr,
                status: v.status,
                actualDate: actualDateStr,
                milestoneName: v.milestoneName,
                isEmptyTest: !v.actualDate,
                confirmationAttachs: v.confirmationAttachs || [],
                isAdvanceInvoice: v.milestoneCode === "start_time" ? false : !!v.isAdvanceInvoice
            }
        })
        
        let circleCompletion: { status: number, milestoneCode: string, milestoneName: string, standardDate: string, actualDate?: string } | undefined
        if (circleCompletionItem) {
            let actualDate = circleCompletionItem.actualDate ? parseTime(circleCompletionItem.actualDate, "{y}-{m}-{d}") : ""
            if(circleCompletionItem.status === k_milestoneStatus.unstart) {
                actualDate = ""
            }
            circleCompletion = {
                milestoneCode: circleCompletionItem.milestoneCode,
                standardDate: circleCompletionItem.standardDate ? parseTime(circleCompletionItem.standardDate, "{y}-{m}-{d}") : "",
                status: circleCompletionItem.status || 0,
                actualDate: actualDate,
                milestoneName: circleCompletionItem.milestoneName
            }
        }
        if (checkStartTimeMilestoneWarn(0)) {
            const firstItem = list[0]
            const firstItemDate = firstItem.opStartTime || ""
            messageApi.error(`项目开始时间不应晚于该项目最早的工时填报时间${firstItemDate}~`);
            return
        }
        if (validateFail) {
            messageApi.error('完成状态的里程碑，实际时间不可为空~');
            return
        }
        if (isLessStartTime) {
            messageApi.error('里程碑时间不得早于开始时间~');
            return
        }
        if (isBeforeLastMilestone) {
            messageApi.error('里程碑时间不得早于前置里程碑时间~');
            return
        }
        if (isCircleUnstart) {
            messageApi.error('请先设置外协交圈时间~');
            return
        }

        // 甲方确认单必传校验
        const missingConfirmation = milestoneList.find(v =>
            v.status === k_milestoneStatus.finished &&
            v.milestoneCode !== "start_time" &&
            v.isRequiredOrg && v.isConfirmationUploaded &&
            (!v.confirmationAttachs || v.confirmationAttachs.length === 0) &&
            v.originalStatus !== k_milestoneStatus.finished
        )
        if (missingConfirmation) {
            messageApi.error('请先上传甲方确认单~');
            return
        }
        
        
        let hasUnFinish = list.find(v => v.status !== k_milestoneStatus.finished && (v.milestoneCode !== "start_time"))

        if (!hasUnFinish) {
            modalApi.confirm({
                title: null,
                icon: null,
                content: <span className="text-[13px]">该项目最后一个里程碑已完成，PLM项目空间中所有未完成任务的状态将被自动修改为“已完成"，是否确认提交?</span>,
                okButtonProps: {
                    danger: true,
                    type: "primary"
                },
                onOk() {
                    if (!props.projectID) return
                    runSaveMilestones(props.projectID, params, props.plotID, invoiceToken, circleCompletion).then(res => {
                        props.onUpdate()
                        props.onCancel()
                    })
                }
            });
            return
        }
        if (!props.projectID) return
        runSaveMilestones(props.projectID, params, props.plotID, invoiceToken, circleCompletion).then(res => {
            props.onUpdate()
            props.onCancel()
        })
        
    }
    // 此权限可编辑已完成状态
    const isOpadmin = useMemo(() => {
        if(props.isPlanList) return true
        if (permissions && permissions.includes(k_permission.opAdmin)) return true
        return false
    }, [permissions, props])
    // 此权限可编辑普通状态
    const hasEditPermission = useMemo(() => {
        if(props.isPlanList) return true
        if(props.isBatch) return true
        if (permissions && (permissions.includes(k_permission.milestoneEdit) || permissions.includes(k_permission.opAdmin))) return true
        return false
    }, [permissions, props])

    // 行的编辑权限 外协交圈不限制
    const getEditPermission = (item: MilestoneEditItem, isCircleNode: boolean = false) => {
        if (isOpadmin || item.isEdit) return true
        if(isCircleNode && hasEditPermission) return true
        if (item.milestoneName === "方案报批通过") {
        }
        // todo 暂时关闭已完成状态的里程碑编辑权限
        if (item.milestoneCode === "start_time") {
            if (item.opStartTime && dayjs(item.milestoneDate).isAfter(dayjs(item.opStartTime))) {
                return true
            }
            if(item.milestoneDate) return false
        }
        if (item.status === k_milestoneStatus.finished && item.actualDate) return false
        if (hasEditPermission) return true
        return false
    }
    // 时间列的编辑限制
    const getEditDatePermission = (item: MilestoneEditItem, isActualDate: boolean = false) => {
        if (isActualDate && item.actualDate) return true
        if(!isActualDate && item.milestoneDate) return true

        const currentIndex = getMilestoneIndex(item.milestoneCode);
        return canEditMilestoneDate(currentIndex, isActualDate)
    }


    const getDefaultDate = (item: API.PROJECT.Milestone) => {
        if(item.actualDate) return item.actualDate
        const curDate = new Date()
        curDate.setHours(0, 0, 0)
        let completedDateStr = parseTime(new Date(), "{y}-{m}-{d}")
        if (item.milestoneDate) {
            const planDate = new Date(item.milestoneDate)
            planDate.setHours(0, 0, 0)
            if (planDate.getTime() < curDate.getTime()) {
                completedDateStr = item.milestoneDate
            }
        }
        return completedDateStr
    }

    const checkIsBeforeLastMilestone = (currentIndex: number, isActualDate: boolean = false) => {
        if (currentIndex <= 0) return false;
        const curItem = list[currentIndex];
        if (!curItem) return false;
        
        const prevIndex = getPrevNonSpecialNodeIndex(list, currentIndex, isActualDate);
        if (prevIndex < 0) return false;
        
        const prevItem = list[prevIndex];
        if (!prevItem) return false;
        
        if (isActualDate) {
            const dateStr = prevItem.actualDate 
            if (!dateStr || !curItem.actualDate) return false;
            return dayjs(curItem.actualDate).isBefore(dayjs(dateStr)) || dayjs(curItem.actualDate).isSame(dayjs(dateStr));
        }
        const dateStr = prevItem.milestoneDate;
        if (!dateStr || !curItem.milestoneDate) return false;

        return dayjs(curItem.milestoneDate).isBefore(dayjs(dateStr)) || dayjs(curItem.milestoneDate).isSame(dayjs(dateStr));
    }
    const checkStartTimeMilestoneWarn = (currentIndex: number) => {
        if (currentIndex !== 0 || !props.isFirstPlot) return false;
        const curItem = list[currentIndex];
        if (!curItem || curItem.milestoneCode !== "start_time" || !curItem.milestoneDate || !curItem.opStartTime) return false;
        return dayjs(curItem.milestoneDate).isAfter(dayjs(curItem.opStartTime))
    }

    const canEditMilestoneDate = (currentIndex: number, isActualDate: boolean = false) => {
        if (currentIndex === 0) return true;
        if(isActualDate && currentIndex === 1) return true
        const prevItem = list[currentIndex - 1];
        if (!prevItem) return true;
        return !!prevItem.milestoneDate;
    };

    const getMilestoneIndex = (milestoneCode: string) => {
        return list.findIndex(v => v.milestoneCode === milestoneCode);
    };
    const [historyModalVisible, setHistoryModalVisible] = useState(false)
    const [currentMilestoneNode, setCurrentMilestoneNode] = useState<API.PROJECT.Milestone>()
    const showHistoryModal = (item: API.PROJECT.Milestone) => {
        setCurrentMilestoneNode(item)
        setHistoryModalVisible(true)
    }
    const onCloseHistoryModal = () => {
        setCurrentMilestoneNode(undefined)
        setHistoryModalVisible(false)
    }
    return (
        <>
            {contextHolder}
            {modalContextHolder}
            <div>
                <div className="flex justify-between items-center pb-3">
                    <div>
                        <div className="text-red-500 text-xs">* 请注意已完成状态的里程碑保存后将不可编辑</div>
                        
                        {/* {showLastMilestoneWarn && (
                            <div className="text-red-500 text-xs mt-1">* 该项目最后一个里程碑已完成，PLM项目空间中所有未完成任务的状态将被自动修改为“已完成”，是否确认提交？</div>
                        )} */}
                    </div>
                    
                    <div className="flex">
                        <Button className="ml-4" onClick={props.onCancel}>
                            取消
                        </Button>
                        <ConfigProvider
                            theme={{
                                token: {
                                    colorPrimary: "#161616",
                                }
                            }}>
                            <Button type="primary" className="ml-4" onClick={onSave} disabled={!isOpadmin && !hasEditPermission} loading={loading}>
                                保存
                            </Button>
                        </ConfigProvider>
                    </div>
                    
                </div>
                <div className="border-0 border-t border-solid border-gray-300">
                    <div className="py-3 flex border-0 border-b border-solid border-gray-300 font-bold gap-1">
                        <div className="flex-1 shrink-0 flex items-center justify-center">里程碑名称</div>
                        <div className="flex-1 shrink-0 flex justify-center">计划时间</div>
                        <div className="flex-1 shrink-0 flex justify-center">实际时间</div>
                        <div className="flex-1 shrink-0 flex justify-center">甲方确认单上传</div>
                        <div className="flex-1 shrink-0 flex justify-center">提前开票</div>
                        <div className="flex-1 shrink-0 flex justify-center">状态</div>
                        <div className="shrink-0 flex justify-center w-[30px]"></div>
                    </div>
                    {list.map((v, index) => (
                        <React.Fragment key={v.milestoneCode}>
                            {v.milestoneCode === k_circleNodeParentCode && (
                                <div className="py-3 flex border-0 border-solid border-gray-300 gap-1">
                                    <div className={`flex-1 shrink-0 flex items-center justify-center`}>外协交圈时间</div>
                                    <div className="flex-1 shrink-0 flex flex-col justify-center">
                                        {!getEditPermission(v, true) ? (
                                            <div>{circleNodeData?.standardDate}</div>
                                        ) : (
                                            <div className="flex items-center">
                                                <span className="text-red-500 mr-1">*</span>
                                                <DatePicker
                                                    showNow={false}
                                                    defaultValue={circleNodeData?.standardDate && dayjs(circleNodeData?.standardDate)}
                                                    value={circleNodeData?.standardDate && dayjs(circleNodeData?.standardDate)}
                                                    onChange={(date, dateStr) => {
                                                        setCircleNodeData((cdata) => {
                                                            return {
                                                                ...cdata,
                                                                standardDate: parseTime(dateStr as string, "{y}-{m}-{d}")
                                                            }
                                                        })
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-1 shrink-0 flex flex-col justify-center">
                                        {!getEditPermission(v, true) ? (
                                            <div>{circleNodeData?.actualDate}</div>
                                        ) : (circleNodeData?.status === k_milestoneStatus.finished && (
                                            <DatePicker
                                                showNow={false}
                                                maxDate={dayjs(currentDate.current)}
                                                defaultValue={dayjs(circleNodeData?.actualDate)}
                                                value={circleNodeData?.actualDate && dayjs(circleNodeData?.actualDate)}
                                                onChange={(date, dateStr) => {
                                                    setCircleNodeData((cdata) => {
                                                        return {
                                                            ...cdata,
                                                            actualDate: parseTime(dateStr as string, "{y}-{m}-{d}")
                                                        }
                                                    })
                                                }}
                                            />
                                        ))} 

                                    </div>
                                    <div className="flex-1 shrink-0 flex justify-center"></div>
                                    <div className="flex-1 shrink-0 flex justify-center"></div>
                                    <div className="flex-1 shrink-0 flex justify-center align-center">
                                        {!getEditPermission(v, true) ? (
                                            <div className={getMilestoneStatusTextColor(circleNodeData?.status || 0)}>{getStatusText(circleNodeData?.status || 0)}</div>
                                        ) : (
                                                <Select options={statusOptions} value={circleNodeData?.status || 0}
                                                    onChange={(val) => {
                                                        setCircleNodeData((cdata) => {
                                                            return {
                                                                ...cdata,
                                                                status: val
                                                            }
                                                        })
                                                    }}
                                                />
                                        )}
                                    </div>
                                    <div className="shrink-0 w-[30px]"></div>
                                </div>
                            )}
                            <div key={v.milestoneCode} className="py-3 flex border-0 border-b border-solid border-gray-300 gap-1">
                                <div className={`flex-1 shrink-0 flex items-center justify-center ${v.isWarn ? 'text-red-500' : ''}`}>{v.milestoneName}</div>
                                <div className="flex-1 shrink-0 flex flex-col justify-center">
                                    {!getEditPermission(v) && !(!v.milestoneDate || checkIsBeforeLastMilestone(index) || checkStartTimeMilestoneWarn(index)) ? (
                                        <div>{v.milestoneDate}</div>
                                    ) : (
                                            <>
                                        <div className="flex items-center">
                                            <span className="text-red-500 mr-1">*</span>
                                            <DatePicker
                                                    className={!v.milestoneDate || checkIsBeforeLastMilestone(index) || checkStartTimeMilestoneWarn(index) ? 'border-red-500' : ''}
                                                showNow={false}
                                                defaultValue={v.milestoneDate && dayjs(v.milestoneDate)}
                                                value={v.milestoneDate && dayjs(v.milestoneDate)}
                                                onChange={(...opt) => onDateChange(v, ...opt, index)}
                                                disabled={!getEditDatePermission(v, false)}
                                                disabledDate={(current) => {
                                                    if (!current) return false;
                                                    if (v.milestoneCode === "start_time" && v.opStartTime && props.isFirstPlot) {
                                                        return current > dayjs(v.opStartTime).startOf('day');
                                                    };
                                                    const minDate = getMinDateForMilestone(list, index, false);
                                                    if (minDate) {
                                                        return current < minDate.startOf('day');
                                                    }
                                                    return false;
                                                }}
                                            />
                                        </div>
                                        {props.isFirstPlot && v.milestoneCode === "start_time" && v.opStartTime && dayjs(v.milestoneDate).isAfter(dayjs(v.opStartTime)) && (
                                                    <div className="text-red-400 text-xs">项目开始时间不应晚于该项目最早的工时填报时间{ v.opStartTime }</div>
                                            )}
                                        </>
                                    )}
                                </div>
                                <div className="flex-1 shrink-0 flex flex-col justify-center">
                                    <div className="flex justify-center">
                                        {!getEditPermission(v) && !checkIsBeforeLastMilestone(index, true) ? (
                                            <div>{v.actualDate}</div>
                                        ) : (
                                            v.status === k_milestoneStatus.finished && v.milestoneCode !== "start_time" && (
                                                <DatePicker
                                                    showNow={false}
                                                    className={checkIsBeforeLastMilestone(index, true) ? 'border-red-500' : ''}
                                                    maxDate={dayjs(currentDate.current)}
                                                    defaultValue={dayjs(getDefaultDate(v))}
                                                    value={v.actualDate && dayjs(v.actualDate)}
                                                    onChange={(...opt) => onActualDateChange(v, ...opt, index)}
                                                    disabled={!getEditDatePermission(v, true)}
                                                    disabledDate={(current) => {
                                                        if (!current) return false;
                                                        const minDate = getMinDateForMilestone(list, index, true);
                                                        const invoiceTime = invoiceTimeLimitMap?.[v.milestoneCode] || ""

                                                        if (minDate && current < minDate.startOf('day')) {
                                                            return true;
                                                        }

                                                        if (invoiceTime) {
                                                            const invoiceDayjs = dayjs(invoiceTime);
                                                            return current > invoiceDayjs.endOf('day');
                                                        }

                                                        return false;
                                                    }}
                                                />
                                            )
                                        )}
                                    </div>
                                    {props.isPlanList && invoiceTimeLimitMap?.[v.milestoneCode] && (
                                        <div className="text-green-500 text-xs mt-1 text-center">开票时间：{invoiceTimeLimitMap?.[v.milestoneCode]}</div>
                                    )}
                                </div>
                                <div className="flex-1 shrink-0 flex justify-center">
                                    {getEditPermission(v) && v.isConfirmationUploaded && v.status === k_milestoneStatus.finished ? (
                                        <div className="flex flex-col items-center gap-1 py-1">
                                            {(v.confirmationAttachs || []).map((att, attIdx) => (
                                                <div key={attIdx} className="flex items-center gap-1 max-w-[120px]">
                                                    {att.edocUrl ? (
                                                        <a href={att.edocUrl} target="_blank" rel="noopener noreferrer" className="text-xs break-all hover:underline">
                                                            {att.fileName}
                                                        </a>
                                                    ):(
                                                        <span className="text-xs break-all" title={att.fileName}>{att.fileName}</span>
                                                    )}
                                                    
                                                    <DeleteOutlined
                                                        className="text-red-400 cursor-pointer text-xs shrink-0"
                                                        onClick={() => removeConfirmationAttach(v.milestoneCode, attIdx)}
                                                    />
                                                </div>
                                            ))}
                                            <Upload
                                                showUploadList={false}
                                                customRequest={(options) => handleUploadFile(options, v.ID, v.milestoneCode)}
                                                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                                            >
                                                <Button type="link" size="small" danger={v.isRequiredOrg && (!v.confirmationAttachs || v.confirmationAttachs?.length <= 0)} icon={<UploadOutlined />}>上传</Button>
                                            </Upload>
                                        </div>
                                    ) : (
                                        <div>-</div>
                                    )}
                                </div>
                                <div className="flex-1 shrink-0 flex justify-center items-center">
                                    {v.milestoneCode === "start_time" ? (
                                        <div>-</div>
                                    ) : (
                                        <Checkbox
                                            checked={!!v.isAdvanceInvoice}
                                            disabled={!getEditPermission(v)}
                                            onChange={(e) => onAdvanceInvoiceChange(e.target.checked, v)}
                                        />
                                    )}
                                </div>
                                <div className="flex-1 shrink-0 flex justify-center align-center">
                                    {!getEditPermission(v) ? (
                                        <div className={getMilestoneStatusTextColor(v.status)}>{v.milestoneCode !== "start_time" && getStatusText(v.status)}</div>
                                    ) : (
                                            (v.milestoneCode !== "start_time" && (v.status === k_milestoneStatus.finished || isLastGroupNodeCompleted(list, index))) && (
                                            <Select options={statusOptions} value={v.status} onChange={(val) => onStatueChange(val, v, index)} />
                                        )
                                    )}
                                    
                                </div>
                                <div className="shrink-0 w-[30px]">
                                    {v.milestoneCode !== "start_time" && (
                                        <div className="flex items-center">
                                            <span className="flex cursor-pointer text-gray-700 rounded-md hover:bg-gray-100 hover:text-gray-900 p-2" onClick={() => showHistoryModal(v)}>
                                                <HistoryOutlined />
                                            </span>
                                        </div>

                                    )}
                                </div>
                            </div>
                        </React.Fragment>
                        
                    ))}
                    {list && list.length <= 0 && (
                        <ThEmpty className="py-4" description={(
                            <div className="text-black/50 mt-4">
                                暂无数据
                            </div>
                        )} />
                    )}
                </div>
            </div>
            <HistoryModal
                projectID={props.projectID || ""}
                plotID={props.plotID}
                visible={historyModalVisible}
                onClose={onCloseHistoryModal}
                milestoneNode={currentMilestoneNode} />
        </>
        
    )
}
export default Index